import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Chip,
  Snackbar,
  Alert,
  Fade,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Avatar,
} from "@mui/material";
import {
  generateWorkflow,
  createWorkflow,
  updateWorkflow,
  refineWorkflowInPlace,
  configureWorkflowSchedule,
  configureWorkflowWebhook,
  getWorkflow,
} from "../../api/agentApi";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import WorkflowRefinementEditor from "./WorkflowRefinementEditor";
import WorkflowGeneratingIndicator from "./WorkflowGeneratingIndicator";

const IntentToWorkflowCreator: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { workflowId } = useParams();
  const [editingWorkflow, setEditingWorkflow] = useState<any>(
    location.state?.workflow ?? null,
  );
  const [workflowLoadError, setWorkflowLoadError] = useState("");
  const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(
    Boolean(workflowId && !location.state?.workflow),
  );
  const editWorkflowId = editingWorkflow?.id ?? workflowId;
  const isEdit = Boolean(workflowId || editingWorkflow);

  // 1. multi-intent data matrix (5 total variants per category)
  const INTENT_MATRIX = {
    RevOps: {
      badge: "RO",
      badgeBg: "#4A90D9",
      variants: [
        "Review our sales pipeline health, flag deals that have stalled in the current stage for too long, and generate an executive summary for high-value accounts.",
        "Reconcile monthly CRM data against billing records, identify any subscription discrepancies, and draft an anomaly report for the finance team.",
        "Monitor contract renewal dates across our enterprise accounts for the upcoming quarter and auto-draft personalized outreach sequences based on historical feature usage.",
        "Audit our lead routing rules to identify drop-offs, track conversion speed across regions, and suggest optimizations to balance sales representative capacity.",
        "Analyze historical deal-stage progression patterns to calculate a weighted win-probability score for the current active pipeline.",
      ],
    },
    Marketing: {
      badge: "MK",
      badgeBg: "#789769",
      variants: [
        "Analyze our latest campaign performance metrics, check for any audience drift, and break down which content variants are driving the highest conversion split.",
        "Extract semantic themes from community forum mentions and social feedback this week, then map them against our current content repository to spot messaging gaps.",
        "Evaluate ad spend attribution across our active digital acquisition channels and compile a reallocation plan targeting the lowest cost-per-acquisition segments.",
        "Review competitive press releases and product launches from the last month, summarizing key strategic threats and messaging pivots our team should make.",
        "Monitor global blog traffic for sudden search ranking drops, run a keyword degradation analysis, and outline optimization tasks for the affected assets.",
      ],
    },
    Research: {
      badge: "RS",
      badgeBg: "#8E588E",
      variants: [
        "Help me design a customer satisfaction survey, automatically categorize and code the open-ended text responses, and summarize key trends over the last three quarters.",
        "Synthesize primary user interview transcripts, extract core friction points during onboarding, and group them into actionable feature requests for product teams.",
        "Perform a longitudinal study compilation on our usability tracking data, highlighting behavioral drop-offs before and after the recent major interface release.",
        "Scan peer-reviewed literature and patent databases regarding multi-agent architectures, outlining novel optimization strategies for production scaling.",
        "Cross-reference survey demographic data with feature engagement statistics to isolate which customer personas experience the fastest time-to-value.",
      ],
    },
  };

  // 2. Track the cycling index state independently for each category label
  const [rotationIndexes, setRotationIndexes] = useState({
    RevOps: 0,
    Marketing: 0,
    Research: 0,
  });

  // 3. Cycle sequentially through prompts when clicked
  const handleChipClick = (category: "RevOps" | "Marketing" | "Research") => {
    if (isDrafting) return;

    const categoryData = INTENT_MATRIX[category];
    const currentIndex = rotationIndexes[category];

    // Inject the current variant text
    setIntent(categoryData.variants[currentIndex]);

    // Advance index to next slot, wrapping around dynamically at the end of the array
    setRotationIndexes((prev) => ({
      ...prev,
      [category]: (currentIndex + 1) % categoryData.variants.length,
    }));
  };

  // --- STATE ---
  const [activeStep, setActiveStep] = useState(isEdit ? 1 : 0);
  const [intent, setIntent] = useState<string>("");

  const [isDrafting, setIsDrafting] = useState(false);
  const [generatedWorkflow, setGeneratedWorkflow] = useState<any>(
    isEdit ? editingWorkflow : null,
  );
  const [workflowName, setWorkflowName] = useState<string>(
    isEdit ? (editingWorkflow?.name ?? "") : "",
  );
  const [triggerDraft, setTriggerDraft] = useState<any>(null);

  const [isCreating, setIsCreating] = useState(false);
  const [notification, setNotification] = useState<any>({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    if (!workflowId) return;
    if (editingWorkflow?.id === workflowId) return;
    if (location.state?.workflow?.id === workflowId) {
      setEditingWorkflow(location.state.workflow);
      return;
    }

    setIsLoadingWorkflow(true);
    setWorkflowLoadError("");
    getWorkflow(workflowId)
      .then((workflow: any) => {
        setEditingWorkflow(workflow);
      })
      .catch((error: any) => {
        setWorkflowLoadError(
          error?.message ?? "Failed to load workflow for refinement.",
        );
      })
      .finally(() => setIsLoadingWorkflow(false));
  }, [workflowId, editingWorkflow?.id, location.state]);

  useEffect(() => {
    if (!editingWorkflow) return;
    setActiveStep(1);
    setGeneratedWorkflow(editingWorkflow);
    setWorkflowName(editingWorkflow?.name ?? "");
    setIntent(editingWorkflow?.intent ?? editingWorkflow?.description ?? "");
  }, [editingWorkflow]);

  const handleDraftIntent = async () => {
    setIsDrafting(true);
    try {
      const response = await generateWorkflow(intent);
      // response shape: { name, dag_definition, intent_history, discovery?, validation? }
      setGeneratedWorkflow(response);
      setWorkflowName(response.name ?? "");
      // Keep `intent` in state so WorkflowRefinementEditor can pass it as original_intent
      // to POST /workflow/refine. Do NOT clear it.
      setActiveStep(1);
    } catch (error: any) {
      setNotification({
        open: true,
        message: `Workflow generation failed: ${error.message ?? "Unknown error"}`,
        severity: "error",
      });
    } finally {
      setIsDrafting(false);
    }
  };

  const handleCreateWorkflow = async () => {
    setIsCreating(true);
    try {
      if (isEdit) {
        // Edit mode: "Done" — just navigate back, refinements were already saved in-place
        navigate("/dashboard");
        return;
      }
      const created = await createWorkflow(
        workflowName,
        generatedWorkflow?.dag_definition ?? generatedWorkflow,
        generatedWorkflow?.intent_history ?? null,
      );

      const selectedSchedule =
        triggerDraft?.mode === "schedule"
          ? triggerDraft.schedule_config
          : !triggerDraft
            ? generatedWorkflow?.suggested_schedule_config
            : null;
      const selectedWebhook =
        triggerDraft?.mode === "webhook"
          ? triggerDraft.webhook_config
          : !triggerDraft
            ? generatedWorkflow?.suggested_webhook_config
            : null;

      // Apply the selected schedule from the review flow, or the generated suggestion
      // when the user never changed trigger settings.
      const suggestedSchedule = selectedSchedule;
      if (suggestedSchedule?.cron_expression && created?.workflow_id) {
        try {
          await configureWorkflowSchedule(created.workflow_id, {
            cron_expression: suggestedSchedule.cron_expression,
            enabled: suggestedSchedule.enabled ?? true,
          });
        } catch (scheduleErr: any) {
          console.warn("Failed to apply suggested schedule:", scheduleErr);
          setNotification({
            open: true,
            message: `Workflow created, but the schedule (${suggestedSchedule.cron_expression}) could not be applied automatically. Configure it from the workflow settings.`,
            severity: "warning",
          });
          navigate("/dashboard");
          return;
        }
      }

      // Auto-create suggested webhook trigger (disabled placeholder — user configures secrets later)
      const suggestedWebhook = selectedWebhook;
      if (suggestedWebhook?.adapter_type && created?.workflow_id) {
        try {
          await configureWorkflowWebhook(created.workflow_id, {
            adapter_type: suggestedWebhook.adapter_type,
            enabled: suggestedWebhook.enabled ?? false,
            secrets_ref: suggestedWebhook.secrets_ref ?? {},
          });
        } catch (webhookErr: any) {
          // Non-fatal — workflow was created, just the webhook placeholder didn't save
          console.warn("Failed to create suggested webhook placeholder:", webhookErr);
        }
      }

      navigate("/dashboard");
    } catch (error: any) {
      setNotification({
        open: true,
        message: `Create failed: ${error.message ?? "Unknown error"}`,
        severity: "error",
      });
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Edit mode only: called by WorkflowRefinementEditor's chat send button.
   * Calls PUT /workflow/{id}/refine which refines the DAG via LLM and saves
   * a new version in one shot — no separate "Update Workflow" button press needed.
   */
  const handleInPlaceRefine = async (
    refinement: string,
  ): Promise<{ updatedDag: any; updatedName: string }> => {
    try {
      if (!editWorkflowId) {
        throw new Error("Workflow id is required to refine workflow.");
      }
      const result = await refineWorkflowInPlace(editWorkflowId, refinement);
      const updatedDag = result?.dag_definition ?? result;
      const updatedName = result?.name ?? workflowName;
      setGeneratedWorkflow({ dag_definition: updatedDag });
      setWorkflowName(updatedName);
      return { updatedDag, updatedName };
    } catch (error: any) {
      throw error;
    }
  };

  /**
   * Edit mode only: saves a name-only change via PUT /workflow/{id}.
   * Keeps the current DAG intact and just updates the name.
   */
  const handleSaveWorkflowName = async (newName: string): Promise<void> => {
    const currentDag = generatedWorkflow?.dag_definition ?? generatedWorkflow;
    if (!editWorkflowId) {
      throw new Error("Workflow id is required to save workflow changes.");
    }
    await updateWorkflow(editWorkflowId, newName, currentDag);
    setWorkflowName(newName);
  };

  const handleCancel = () => {
    if (activeStep === 1 && !isEdit) {
      setActiveStep(0);
      setGeneratedWorkflow(null);
      setWorkflowName("");
    } else {
      navigate("/workflows");
    }
  };

  const dagDefinition = generatedWorkflow?.dag_definition ?? generatedWorkflow;

  if (isEdit && isLoadingWorkflow) {
    return (
      <Box sx={{ flex: 1, display: "grid", placeItems: "center", p: 4 }}>
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading workflow for refinement...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isEdit && workflowLoadError) {
    return (
      <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 3 }}>
          {workflowLoadError}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, overflow: "auto", px: 3, pt: 2 }}>
      <Box>
        <Typography variant="h4">
          {isEdit ? "Edit Workflow" : "Create Workflow"}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1, px: 0.5 }}
        >
          {isEdit
            ? "Update your workflow's name or regenerate the DAG from a new intent."
            : "Describe what you want the workflow to do and we'll generate the DAG for you."}
        </Typography>
      </Box>

      {/* STEPPER */}
      {!isEdit && (
        <Box
          sx={{ mb: 4, mt: 3, display: "flex", justifyContent: "flex-start" }}
        >
          <Stepper
            activeStep={activeStep}
            sx={{ width: "100%", maxWidth: "500px" }}
          >
            <Step>
              <StepLabel>Draft</StepLabel>
            </Step>
            <Step>
              <StepLabel>Review</StepLabel>
            </Step>
          </Stepper>
        </Box>
      )}

      {/* STEP 0: DEFINE INTENT */}
      {!isEdit && activeStep === 0 && (
        <Fade in={activeStep === 0}>
          <Box>
            <Paper
              elevation={0}
              sx={{
                px: 3,
                py: 2,
                mb: 2,
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "action.hover",
              }}
            >
              <Card
                sx={{
                  p: 3,
                  borderRadius: 4,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, ml: 1, fontWeight: 700 }}
                >
                  Workflow Intent
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  placeholder="E.g., 'Run a research agent to gather information on a given topic, then produce a polished report ready for sharing.'"
                  value={intent}
                  onChange={(e) => setIntent(e.target.value)}
                  variant="filled"
                  sx={{ bgcolor: "background.paper", borderRadius: 2 }}
                />
               {/* ── Rotating Chips Layout ────────────────────────── */}
            <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
              <Box 
                sx={{ 
                  display: "flex", 
                  flexWrap: "wrap", 
                  justifyContent: "center", 
                  gap: 1.5,
                  maxWidth: "90%"
                }}
              >
                {(Object.keys(INTENT_MATRIX) as Array<keyof typeof INTENT_MATRIX>).map((label) => {
                  const targetConfig = INTENT_MATRIX[label];
                  return (
                    <Chip
                      key={label}
                      icon={
                        <Avatar 
                          sx={{ 
                            width: 20, 
                            height: 20, 
                            fontSize: '0.65rem', 
                            fontWeight: 700,
                            p: 0.36,
                            bgcolor: targetConfig.badgeBg, 
                            color: '#ffffff !important',
                            ml: '4px !important'
                          }}
                        >
                          {targetConfig.badge}
                        </Avatar>
                      }
                      // Display dynamic counter inside label to hint at the rotation capability (e.g., "RevOps (1/5)")
                      label={`${label} (${rotationIndexes[label] + 1}/5)`}
                      onClick={() => handleChipClick(label)}
                      disabled={isDrafting}
                      variant="outlined"
                      sx={{
                        borderRadius: "99px",
                        py: 2.2,
                        px: 0.9,
                        fontSize: "0.85rem",
                        fontWeight: 500,
                        color: "#475569",
                        borderColor: "rgba(0, 0, 0, 0.27)",
                        bgcolor: "#ffffff",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: "pointer",
                        '&:hover': {
                          bgcolor: "#f8fafc",
                          borderColor: "primary.main", // Green/Blue/Purple bounds to Primary on focus highlight
                          transform: "translateY(-1px)",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04)",
                        },
                        '&:active': {
                          transform: "translateY(0px)"
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
            {/* ─────────────────────────────────────────────────────── */}
              </Card>
            </Paper>
            <Box
              sx={{
                mt: 4,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 3,
              }}
            >
              {isDrafting && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <WorkflowGeneratingIndicator />
                </Box>
              )}
              <Button
                size="large"
                variant="contained"
                color="primary"
                onClick={handleDraftIntent}
                disabled={isDrafting || !intent.trim()}
                startIcon={
                  isDrafting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <AccountTreeOutlinedIcon />
                  )
                }
                sx={{
                  borderRadius: 99,
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  boxShadow: "0 10px 30px rgba(25, 118, 210, 0.3)",
                }}
              >
                {isDrafting ? "Generating..." : "Generate"}
              </Button>
            </Box>
          </Box>
        </Fade>
      )}

      {/* STEP 1: REVIEW GENERATED WORKFLOW */}
      {(isEdit || activeStep === 1) && generatedWorkflow && (
        <Fade in>
          <Box sx={{ mt: isEdit ? 3 : 0 }}>
            <WorkflowRefinementEditor
              workflowName={workflowName}
              onWorkflowNameChange={setWorkflowName}
              dagDefinition={dagDefinition}
              isEdit={isEdit}
              isCreating={isCreating}
              onConfirm={handleCreateWorkflow}
              onCancel={handleCancel}
              intent={intent}
              onIntentChange={setIntent}
              initialIntentHistory={generatedWorkflow?.intent_history ?? null}
              onDagRefined={(updatedDag, updatedName, intentHistory) => {
                setGeneratedWorkflow((prev: any) => ({
                  ...prev,
                  dag_definition: updatedDag,
                  ...(intentHistory ? { intent_history: intentHistory } : {}),
                }));
                setWorkflowName(updatedName);
              }}
              // In edit mode: each refinement is saved immediately via PUT /workflow/{id}/refine
              onRefinementRequested={isEdit ? handleInPlaceRefine : undefined}
              // In edit mode: allow saving a name-only change via PUT /workflow/{id}
              onWorkflowNameSave={isEdit ? handleSaveWorkflowName : undefined}
              // In edit mode: pass workflowId so the editor can load/save trigger config
              workflowId={isEdit ? editWorkflowId : undefined}
              // In edit mode: seed schedule config from already-fetched workflow data
              initialScheduleConfig={isEdit ? editingWorkflow?.schedule_config ?? null : null}
              // Create mode: pass suggestions so editor can show info banners
              suggestedScheduleConfig={!isEdit ? (generatedWorkflow?.suggested_schedule_config ?? null) : null}
              suggestedWebhookConfig={!isEdit ? (generatedWorkflow?.suggested_webhook_config ?? null) : null}
              onTriggerDraftChange={!isEdit ? setTriggerDraft : undefined}
            />
          </Box>
        </Fade>
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert severity={notification.severity} variant="filled">
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IntentToWorkflowCreator;
